import { useCallback, useEffect, useMemo } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import localStorageManager from '@App/cookies/local_storage_manager'
import {
  selectActiveWatchlistId,
  selectOrder,
  selectSelectedTab,
  selectWatchlists,
} from '@App/redux/slices/watchlist/selectors'
import {
  reorderWatchlists,
  moveMarket,
  resetToDefaults,
  setActiveWatchlist,
  setSelectedTab,
  updateWatchlistItems,
  Watchlist,
  WatchlistItem,
} from '@App/redux/slices/watchlist/slice'

import {
  sendCreateWatchlistConnection,
  sendDeleteWatchlistConnection,
  sendUpdateWatchlistConnection,
  sendUpdateWatchlistOrdersConnection,
} from '@Redux/slices/sockets/ProxyServiceConnectionSlice'
import { UpdateWatchlistOperation } from '@Redux/slices/user/types'

import { IShowMarketsUpdated } from '@UI/crypto_exchange/desktop/pages/spot/components/SpotOrderMarketsTable'
import { ICountry } from '@Redux/slices/config/interfaces'
import { fiatFilter, selectSharedCountries } from '@Redux/slices/config/config.selectors'

const EMPTY_MARKETS: IShowMarketsUpdated[] = []

export const useWatchlists = (
  options: {
    markets?: IShowMarketsUpdated[]
    showPortfolio?: boolean
    sharedCountries?: ICountry[]
  } = {},
) => {
  const { markets = EMPTY_MARKETS, showPortfolio } = options

  const dispatch = useDispatch()

  const watchlistsMap = useSelector(selectWatchlists)
  const order = useSelector(selectOrder)
  const selectedTab = useSelector(selectSelectedTab)
  const activeWatchlistId = useSelector(selectActiveWatchlistId)

  const actions = useMemo(
    () => ({
      // Toggle активности
      toggleActive: (key: string) => {
        const watchlist = watchlistsMap?.[key]
        if (!watchlist) return

        dispatch(sendUpdateWatchlistConnection({
          key,
          operation: 'change_status',
          is_enabled: !watchlist.is_enabled
        }))
      },

      // Установить активность
      setActive: (key: string, isActive: boolean) => {
        const watchlist = watchlistsMap?.[key]
        if (!watchlist) return

        dispatch(
          sendUpdateWatchlistConnection({
            key,
            operation: UpdateWatchlistOperation.ChangeStatus,
            is_enabled: isActive,
          }),
        )
      },

      reorder: (newOrder: string[]) => {
        dispatch(reorderWatchlists(newOrder))
        dispatch(sendUpdateWatchlistOrdersConnection(newOrder))
      },

      // Создать кастомный watchlist
      create: (name: string, marketIds?: number[]) =>
        dispatch(
          sendCreateWatchlistConnection({
            name,
            market_ids: marketIds ?? [],
          }),
        ),

      // Удалить кастомный watchlist
      delete: (key: string) => dispatch(sendDeleteWatchlistConnection(key)),

      rename: (key: string, name: string) => {
        const watchlist = watchlistsMap?.[key]
        if (!watchlist) return

        dispatch(
          sendUpdateWatchlistConnection({
            key,
            operation: UpdateWatchlistOperation.ChangeName,
            name,
          }),
        )
      },

      addMarket: (watchlistId: string, marketId: number) => {
        const watchlist = watchlistsMap?.[watchlistId]
        if (!watchlist) return

        dispatch(
          sendUpdateWatchlistConnection({
            key: watchlistId,
            operation: UpdateWatchlistOperation.AddMarket,
            market_id: marketId,
          }),
        )
      },

      removeMarket: (watchlistId: string, marketId: number) => {
        const watchlist = watchlistsMap?.[watchlistId]
        if (!watchlist) return

        dispatch(
          sendUpdateWatchlistConnection({
            key: watchlistId,
            operation: UpdateWatchlistOperation.DeleteMarket,
            market_id: marketId,
          }),
        )
      },

      moveMarket: (watchlistId: string, marketId: number, order: number) => {
        const watchlist = watchlistsMap?.[watchlistId]
        if (!watchlist) return

        dispatch(moveMarket({ watchlistId, marketId, order }))
        dispatch(
          sendUpdateWatchlistConnection({
            key: watchlistId,
            operation: UpdateWatchlistOperation.MoveMarket,
            market_id: marketId,
            order,
          }),
        )
      },

      addMarkets: (watchlistId: string, marketIds: number[]) => {
        const watchlist = watchlistsMap?.[watchlistId]
        if (!watchlist) return

        marketIds.forEach(marketId => {
          dispatch(sendUpdateWatchlistConnection({
            key: watchlistId,
            operation: UpdateWatchlistOperation.MoveMarket,
            market_id: marketId,
          }))
        })
      },

      // Установить маркеты
      setMarkets: (watchlistId: string, marketIds: number[]) => {
        const watchlist = watchlistsMap?.[watchlistId]
        if (!watchlist) return

        watchlist.market_ids
          .filter(id => !marketIds.includes(id))
          .forEach(marketId => {
            dispatch(sendUpdateWatchlistConnection({
              key: watchlistId,
              operation: 'delete_market',
              market_id: marketId,
            }))
          })

        marketIds
          .filter(id => !watchlist.market_ids.includes(id))
          .forEach(marketId => {
            dispatch(sendUpdateWatchlistConnection({
              key: watchlistId,
              operation: 'add_market',
              market_id: marketId,
            }))
          })
      },

      // Выбрать tab
      setSelectedTab: (tab: string | null) => dispatch(setSelectedTab(tab)),

      setActiveForEdit: (id: string | null) => dispatch(setActiveWatchlist(id)),

      updateItems: (watchlistId: string, items: WatchlistItem[]) =>
        dispatch(updateWatchlistItems({ watchlistId, items })),

      reset: () => dispatch(resetToDefaults()),
    }),
    [dispatch, watchlistsMap],
  )

  const sortedWatchlists = order.map((id) => watchlistsMap[id]).filter(Boolean)
  const activeWatchlists = sortedWatchlists.filter((w) => w.is_enabled)
  const customWatchlists = sortedWatchlists.filter((w) => w.isCustom)

  const fiatFilterData = useSelector(fiatFilter)
  const availableMarketTypes = useMemo(() => {
    const types = new Set(markets.map((market) => market.type))

    if (showPortfolio) {
      types.add('portfolio')
    }

    types.add('top_risers')
    types.add('top_fallers')
    types.add('fiat')
    types.add('crypto')
    types.add('popular')
    types.add('most_volatile')
    types.add('commods')
    types.add('indexes')
    types.add('most_traded')
    types.add('interest_rates')
    types.add('bonds')
    types.add('stocks')

    return Array.from(types)
  }, [markets, showPortfolio])

  useEffect(() => {
    if (!selectedTab && availableMarketTypes.length > 0) {
      const savedTab = localStorageManager.get('selectedMarketTab')
      const tabToSet = savedTab && availableMarketTypes.includes(savedTab) ? savedTab : availableMarketTypes[0]
      dispatch(setSelectedTab(tabToSet))
    }
  }, [availableMarketTypes, selectedTab, dispatch])

  const filteredWatchlists = useMemo(() => {
    return activeWatchlists.filter((watchlist) => {
      if (watchlist.isCustom) return true
      if (watchlist.key && availableMarketTypes.includes(watchlist.key)) return true
      return watchlist?.items?.some((item) => item.value && availableMarketTypes.includes(item.value))
    })
  }, [activeWatchlists, availableMarketTypes])

  const forexItems = useMemo(() => {
    if (!fiatFilterData.length) return []

    return fiatFilterData.map((filter): WatchlistItem => ({
      key: filter.key,
      name: filter.name,
      value: filter.key,
      isSearchWord: true,
      icon: filter.key.slice(0, 2).toUpperCase(),
    }))
  }, [fiatFilterData])

  useEffect(() => {
    if (forexItems.length > 0) {
      dispatch(updateWatchlistItems({ watchlistId: 'fiat', items: forexItems }))
    }
  }, [forexItems, dispatch])

  const countryKeysArray = useSelector(selectSharedCountries)
  const countryKeysSet = useMemo(() => new Set(countryKeysArray.map(country => country.key.toLowerCase())), [countryKeysArray])

   const extractStocksData = (markets: IShowMarketsUpdated[]) => {
    const stocksMarkets = markets.filter(({ type, source_country }) =>
      source_country != null
    )

    return {
      countryKeys: countryKeysSet,
      stocksMarkets
    }
  }

  const stockItems = useMemo(() => {
    const { countryKeys } = extractStocksData(markets)

    const popularStocks: WatchlistItem = {
      key: 'popular_shares',
      name: 'Popular shares',
      value: 'stocks',
      icon: '',
      subfilters: true,
    }

    const countryItems = options.sharedCountries ? options.sharedCountries
      .filter((country) => countryKeys.has(country.key.toLowerCase()))
      .map((country): WatchlistItem => ({
        key: country.key.toLowerCase(),
        name: country.name,
        value: `stocks|${country.key.toLowerCase()}`,
        isSearchWord: false,
        icon: country.key.toLowerCase(),
        subfilters: [
          {
            key: `${country.key.toLowerCase()}-most_traded`,
            name: 'Most traded',
            value: `country-${country.key.toLowerCase()}-most_traded`,
            icon: 'most_traded',
          },
          {
            key: `${country.key.toLowerCase()}-top_risers`,
            name: 'Top risers',
            value: `country-${country.key.toLowerCase()}-gainer`,
            icon: 'top_risers',
          },
          {
            key: `${country.key.toLowerCase()}-top_fallers`,
            name: 'Top fallers',
            value: `country-${country.key.toLowerCase()}-loser`,
            icon: 'top_fallers',
          },
          {
            key: `${country.key.toLowerCase()}-most_volatile`,
            name: 'Most volatile',
            value: `country-${country.key.toLowerCase()}-most_volatile`,
            icon: 'most_volatile',
          },
        ],
      })) : []
    return [popularStocks, ...countryItems]
  }, [options.sharedCountries, countryKeysSet, markets])

  useEffect(() => {
    if (stockItems.length > 0) {
      dispatch(updateWatchlistItems({ watchlistId: 'stocks', items: stockItems }))
    }
    }, [stockItems, dispatch])

  const getWatchlistMarkets = useCallback(
    (watchlistId: string) => {
      const watchlist = watchlistsMap[watchlistId]
      if (!watchlist) return []

      return markets.filter((market) => watchlist.market_ids.includes(market.id))
    },
    [watchlistsMap, markets],
  )

  const isMarketInWatchlist = (watchlistId: string, marketId: number) => {
    const watchlist = watchlistsMap[watchlistId]

    return watchlist?.market_ids.includes(marketId) ?? false
  }

  const getWatchlist = (id: string): Watchlist | undefined => {
    return watchlistsMap[id]
  }

  const handleSelectTab = (sectionValue: string, item: WatchlistItem) => {
    const tab = item.isSearchWord ? `${sectionValue}-${item.value}` : item.value ?? ''
    dispatch(setSelectedTab(tab))
  }

  const getSelectedTabWatchlist = () => {
    if (!selectedTab) return null

    if (watchlistsMap[selectedTab]) {
      return watchlistsMap[selectedTab]
    }

    const baseId = selectedTab.split('-')[0]
    if (watchlistsMap[baseId]) {
      return watchlistsMap[baseId]
    }

    const found = Object.values(watchlistsMap).find(
      (w) => w.key === selectedTab || w.key.startsWith(selectedTab + '-'),
    )

    return found ?? null
  }

  return {
    watchlists: sortedWatchlists,
    activeWatchlists,
    filteredWatchlists,
    customWatchlists,
    selectedTab,
    activeWatchlistId,
    availableMarketTypes,
    forexItems,

    ...actions,
    getSelectedTabWatchlist,
    getWatchlist,
    getWatchlistMarkets,
    isMarketInWatchlist,
    handleSelectTab,
  }
}

export default useWatchlists
